import {
  ContextualMenu,
  IconButton,
  mergeStyleSets,
  Modal,
  PrimaryButton,
  Stack,
  Text,
  useTheme,
} from '@fluentui/react';
import React, { useMemo } from 'react';

export type InviteException = {
  id: string;
  name: string;
  email?: string | null;
  error: string;
};

type Props = {
  exceptions: InviteException[];
  onDismiss: () => void;
};

export const InviteExceptionsModal: React.FC<Props> = ({
  exceptions,
  onDismiss,
}) => {
  const { palette } = useTheme();

  const classNames = useMemo(
    () =>
      mergeStyleSets({
        header: {
          padding: '16px 20px 8px',
        },
        body: {
          padding: '0 20px',
          width: 560,
          maxWidth: '90vw',
          maxHeight: 320,
          overflowY: 'auto',
        },
        row: {
          padding: '6px 0',
          borderBottom: `1px solid ${palette.neutralLight}`,
        },
        userCol: { paddingRight: 12, wordBreak: 'break-word' },
        errorCol: { width: 200, wordBreak: 'break-word' },
        headRow: {
          padding: '6px 0',
          fontWeight: 600,
          position: 'sticky',
          top: 0,
          background: palette.white,
          color: palette.neutralPrimary,
          borderBottom: `1px solid ${palette.neutralTertiaryAlt}`,
        },
        footer: {
          padding: '16px 20px',
        },
      }),
    [palette],
  );

  return (
    <Modal
      isOpen
      onDismiss={onDismiss}
      styles={{ scrollableContent: { overflowY: 'hidden' } }}
      dragOptions={{
        moveMenuItemText: 'Move',
        closeMenuItemText: 'Close',
        menu: ContextualMenu,
        dragHandleSelector: '.ms-Modal-scrollableContent > div:first-child',
      }}
    >
      <Stack
        horizontal
        horizontalAlign="space-between"
        verticalAlign="center"
        className={classNames.header}
      >
        <Text variant="xLarge">Exceptions:</Text>
        <IconButton
          iconProps={{ iconName: 'Cancel' }}
          ariaLabel="Close"
          onClick={onDismiss}
        />
      </Stack>

      <Stack className={classNames.body}>
        <Stack horizontal className={classNames.headRow}>
          <Stack.Item grow className={classNames.userCol}>
            User
          </Stack.Item>
          <Stack.Item className={classNames.errorCol}>Error</Stack.Item>
        </Stack>
        {exceptions.map((item) => (
          <Stack horizontal key={item.id} className={classNames.row}>
            <Stack.Item grow className={classNames.userCol}>
              {item.name}
              {item.email ? ` (${item.email})` : ''}
            </Stack.Item>
            <Stack.Item className={classNames.errorCol}>{item.error}</Stack.Item>
          </Stack>
        ))}
      </Stack>

      <Stack horizontalAlign="end" className={classNames.footer}>
        <PrimaryButton text="OK" onClick={onDismiss} />
      </Stack>
    </Modal>
  );
};